import {
  CellDoubleClickedEvent,
  ColDef,
  GridOptions,
} from '@ag-grid-community/core'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  HeliosColumnTypes,
  heliosDefaultColDef,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { CounterParty } from '../../../../model/counter-party.model'
import { Security } from '../../../../model/security.model'
import { CellMatched } from '../../../../model/single-transaction/cell-matched.model'
import { TradeEntryMatchRow } from '../../../../model/single-transaction/trade/trade-entry-match.model'
import { TransactionMatch } from '../../../../model/single-transaction/transaction-match'
import { Calculators } from '../../../../util/calculators.util'
import { createTradeEntryMatchFromTransactionMatch } from '../../../../util/trade-data-mapper-util'

@Component({
  selector: 'hs-trade-matches',
  templateUrl: './trade-matches.component.html',
  styleUrls: ['./trade-matches.component.scss'],
})
export class TradeMatchesComponent implements OnInit {
  @Input() matches?: TransactionMatch[]
  @Input() counterPartyList?: CounterParty[]
  @Input() security?: Security
  @Input() accountDisplayName?: string

  @Input() set applyHPL(applyHPL: boolean) {
    this._applyHPL = applyHPL
    this.gridOptions = this.createGridOptions()
  }

  combinedFactor = 1.0
  loading = true
  _applyHPL?: boolean

  @Output() matchCellDoubleClick = new EventEmitter<CellMatched>()

  rowData: TradeEntryMatchRow[] = []
  tradeEntryField = composeFieldPath(this.rowData)

  gridOptions: GridOptions = this.createGridOptions()

  gridApi?: AgGridApi

  ngOnInit() {
    if (this.security)
      this.combinedFactor = Calculators.calculateCombinedFactor(this.security)
    this.rowData = this.createFreshRowData()
    this.loading = false
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => {
        this.gridApi = event
        event.api.setDomLayout('autoHeight')
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: false,
      },
      sideBar: false,
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        this.matchCellDoubleClick.emit({
          field: event.column.getColId(),
          value: event.data[event.column.getColId()],
        })
      },
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.createColumnDefs(),
      onFirstDataRendered: params => {
        params.api.sizeColumnsToFit()
      },
    }
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return ['copy']
  }

  private createColumnDefs(): ColDef[] {
    const colDef = [
      {
        field: this.tradeEntryField('account'),
        type: NfColumnTypes.Identifier,
        width: 220,
      },
      {
        field: this.tradeEntryField('transactionType'),
        type: NfColumnTypes.Identifier,
        width: 230,
      },
      {
        field: this.tradeEntryField('entryDate'),
        type: NfColumnTypes.Identifier,
        width: 90,
      },

      {
        field: this.tradeEntryField('settleDate'),
        type: NfColumnTypes.Identifier,
        width: 90,
      },
      {
        field: this.tradeEntryField('postDate'),
        type: NfColumnTypes.Identifier,
        width: 90,
      },
      {
        field: this.tradeEntryField('cusip'),
        type: NfColumnTypes.Identifier,
        width: 100,
      },
      {
        field: this.tradeEntryField('price'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('units'),
        type: this._applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('commission'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('originationFee'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('prepaidInterest'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('interestDue'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('factor'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('decimalAccrued'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('dollarAccrued'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('priceScalar'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('contractSize'),
        type: NfColumnTypes.Number,
        width: 70,
      },
      {
        field: this.tradeEntryField('proceeds'),
        type: NfColumnTypes.Number,
        width: 110,
      },
      {
        field: this.tradeEntryField('counterParty'),
        type: NfColumnTypes.Identifier,
        width: 110,
      },
      {
        field: this.tradeEntryField('inventoryType'),
        type: NfColumnTypes.Identifier,
        width: 110,
      },
    ]
    return colDef
  }

  createFreshRowData(): TradeEntryMatchRow[] {
    const matchRowData: TradeEntryMatchRow[] = []

    this.matches?.forEach(match => {
      if (
        this.counterPartyList &&
        this.security &&
        this.combinedFactor &&
        this.accountDisplayName
      ) {
        matchRowData.push(
          createTradeEntryMatchFromTransactionMatch(
            match,
            this.accountDisplayName,
            this.security,
            this.counterPartyList,
            Calculators.calculateUnits(
              match,
              this.security,
              this.combinedFactor,
            ),
            this.combinedFactor,
          ),
        )
      }
    })
    return matchRowData
  }
}
